import { Bookmark, Check, X } from "lucide-react";
import { useState } from "react";
import { generationCompletionCopy } from "../../data/navigation/generationExitConfirm";
import { navigateToRoute } from "../../routes/navigateToRoute";
import { runHeadlessLibrarySave } from "../../utils/library/runHeadlessLibrarySave";
import { setPendingJobView } from "../../utils/generation/pendingJobView";
import { resolveToolRouteHash } from "../../utils/generation/resolveToolRouteHash";

export function GenerationCompletionToast({ toast, onDismiss }) {
  const [isSaving, setIsSaving] = useState(false);

  if (!toast) {
    return null;
  }

  const copy = generationCompletionCopy;
  const showSaveAction = !toast.saveToLibrary;

  function handleViewContent() {
    onDismiss?.(toast.jobId);
    const hash = resolveToolRouteHash(toast.toolId);
    setPendingJobView({
      jobId: toast.jobId,
      toolId: toast.toolId,
      request: toast.request ?? undefined,
    });
    if (hash) {
      navigateToRoute(hash);
    }
  }

  function handleGoToLibrary() {
    navigateToRoute("#library");
    onDismiss?.(toast.jobId);
  }

  async function handleSave() {
    if (!toast.result || isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      await runHeadlessLibrarySave({
        toolId: toast.toolId,
        result: toast.result,
      });
      onDismiss?.(toast.jobId);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-x-4 bottom-4 z-[95] mx-auto w-full max-w-sm rounded-xl border border-[#e5e7eb] bg-white p-3.5 shadow-[0_8px_24px_rgba(15,23,42,0.12)] sm:inset-x-auto sm:left-4 sm:right-auto"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary-soft"
          aria-hidden
        >
          <Check className="size-4 text-primary" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-[#111827]">{copy.title}</p>
              <p className="mt-0.5 text-xs leading-5 text-[#434655]">
                {copy.description}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onDismiss?.(toast.jobId)}
              className="shrink-0 rounded-md p-0.5 text-[#64748b] hover:bg-[#f8fafc] hover:text-[#111827]"
              aria-label="Dismiss notification"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={handleViewContent}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-hover"
            >
              {copy.viewContentLabel}
            </button>
            <button
              type="button"
              onClick={handleGoToLibrary}
              className="rounded-md border border-primary bg-white px-3 py-1.5 text-xs font-semibold text-[#434655] hover:bg-primary-soft/60"
            >
              {copy.goToLibraryLabel}
            </button>
            {showSaveAction ? (
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !toast.result}
                className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-[#e2e8f0] bg-[#f8fafc] px-3 py-1.5 text-xs font-semibold text-[#434655] hover:bg-[#f1f5f9] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Bookmark className="size-3.5" aria-hidden />
                {isSaving ? "Saving..." : copy.saveLabel}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
